import { fg } from '@atlaskit/platform-feature-flags';
import { SORT_FIELD_KEY } from '@atlassian/search-client';
import { getBackendExperiment } from '@atlassian/search-experiment';

import {
	DEFAULT_ENTITY_ATI,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../../common/constants/entities';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import {
	PrimaryProductKeys,
	productsSupportingEmptyQuery,
} from '../../../common/constants/products';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/utils';
import { isHello } from '../../../common/utils/is-hello';
import { getSelectedFilters } from '../../../controllers/filters/selected';
import { getJiraFiltersForResultsCountQuery } from '../../../controllers/results/utils';
import type { SelectedInputFilter } from '../../../controllers/store/dropdown';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { searchResultsQuery$variables } from '../search-results/__generated__/searchResultsQuery.graphql';

/**
 * Gets the variables for both search results and right panel queries.
 */
export const getVariables = (
	bootstrap: BootstrapVariables,
	search: SearchVariables,
	selectedInputFilters: SelectedInputFilter[],
	overrideSearchSessionId?: string | null,
): [searchResultsQuery$variables, rightPanelQuery$variables] => {
	const {
		cloudId = '',
		primaryProduct,
		thirdPartyConfigs,
		experimentConfig,
		products,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
	} = bootstrap;
	const {
		searchQuery,
		originalQuery,
		resultsSort: sort,
		filters,
		selectedProducts,
		selectedDatasource,
		jsmIncidentFilterIds,
		paginationTokens,
	} = search;

	const searchSessionId = overrideSearchSessionId || bootstrap.searchSessionId;
	const fetchFromCursor = paginationTokens[0]?.cursor;

	const isEmptyQuery = !searchQuery || searchQuery.trim() === '';
	const availableProducts = products.filter(
		// If the query is empty, only some products are valid
		(product) => !isEmptyQuery || productsSupportingEmptyQuery.includes(product),
	);

	const {
		selectedFilters,
		entities,
		unselectedProductsThirdPartyFilters,
		entitiesForResultsCountQuery,
	} = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts: products,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		jsmIncidentFilterIds,
	});

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience: SEARCH_PAGE,
		tenantId: cloudId,
		isJiraIssueIndexed,
		thirdPartyFilters: selectedFilters.thirdPartyFilters,
	});

	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};

	const limit =
		entities.length === DEFAULT_ENTITY_ATI.length
			? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
			: RESULT_LIMIT_FOR_SINGLE_PRODUCT;

	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
	} = selectedFilters;

	const sortVariables = sort.field === SORT_FIELD_KEY.RELEVANCE ? [] : [sort];

	// If the original query param is present, we want to disable query replacement
	const disableQueryReplacement = !!originalQuery;

	// Filter out query input filter text from search query
	let filteredSearchQuery = searchQuery;
	for (const inputFilter of selectedInputFilters) {
		filteredSearchQuery = filteredSearchQuery.replace(inputFilter.displayText, '');
	}
	filteredSearchQuery = filteredSearchQuery.trim();

	const query = fg('enable_in_query_contributor_filter') ? filteredSearchQuery : searchQuery;
	const queryVersion = 1;
	const cloudIdARI =
		primaryProduct === PrimaryProductKeys.Jira && fg('jira_site_ari_query_enabled')
			? `ari:cloud:jira::site/${cloudId}`
			: `ari:cloud:confluence::site/${cloudId}`;
	const experience = SEARCH_PAGE;
	const tenantId = cloudId;

	const sessionId = getSessionIdFromLocalStorage();

	const fanoutExperimentCohort =
		selectedProducts.length === 0 && fg('enable_linked_results')
			? 'boosted-and-smartlinks'
			: undefined;

	// To ensure rightPanel query matches as much as possible, we inherit+override both from shared variables
	const sharedQueryVariables = {
		cloudIdARI,
		commonFilters,
		confluenceFilters,
		entities,
		experience,
		jiraFilters,
		limit,
		mercuryFilters,
		compassFilters,
		query,
		queryVersion,
		searchSessionId,
		sort: sortVariables,
		sourceProduct: primaryProduct,
		thirdPartyFilters,
		sessionId,
		tenantId,
		disableQueryReplacement,
		...(fg('assets_rovo_search_all') ? { assetsFilters } : {}),
	};

	const searchResultsQueryVariables: searchResultsQuery$variables = {
		...sharedQueryVariables,
		// pagination is only valid for results
		after: fetchFromCursor,
		// highlighting is only useful for results
		enableHighlighting: !!fg('use_backend_highlighting_compass_townsquare'),
		// Experimentation is only valid in results section. Note that experiments can differ based on selected entites/products, so if we include experiments in counts, the counts will change as filters change
		experimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
			name,
			layerId,
			shadowId,
			definitions,
		})),
		shadowExperimentId,
		fanoutExperimentCohort,
		includeIssueTypeAvatarUrl: fg('rovo_search_jira_icons_fps'),
		includeIconUrl: fg('rovo_search_confluence_icons_fps'),
		includePositions: fg('talent_global_search'),
	};

	const rightPanelQueryVariables: rightPanelQuery$variables = {
		...sharedQueryVariables,
		// Limit and sort are irrelvant for counts
		limit: 0,
		sort: [],
		// Counts searches for approx all entities (see getSelectedFilters for edge cases)
		entities: entitiesForResultsCountQuery,
		thirdPartyFilters: unselectedProductsThirdPartyFilters,
		// We need to search for all jira product types to support counts in Jira/JSM/JPD correctly
		jiraFilters: getJiraFiltersForResultsCountQuery(jiraFilters, availableProducts),
	};

	return [searchResultsQueryVariables, rightPanelQueryVariables];
};
